import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  addProcessingSoldDataToBusiness,
  getProcessingSoldDataList,
  updateProcessingSoldData,
  deleteProcessingSoldData,
} from "../../../../../../apis/Business/ProcessingSoldIntermediateDataApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";

const ProcessingDataTable = ({ businessId }) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getProcessingSoldDataList({
      businessId,
      setObjects: setData,
    });
  }, [businessId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEdit = useCallback(
    (editedItem) => {
      updateProcessingSoldData({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        manufacturing_process: editedItem.manufacturing_process,
        sold_intermediate_product: editedItem.sold_intermediate_product,
        manuel: editedItem.manuel,
        weight_final_product: editedItem.weight_final_product,
        final_products: editedItem.final_products,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [businessId, data, setData]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addProcessingSoldDataToBusiness({
        businessId,
        ...newItem,
        manufacturing_process: newItem.manufacturing_process,
        sold_intermediate_product: newItem.sold_intermediate_product,
        manuel: newItem.manuel,
        weight_final_product: newItem.weight_final_product,
        final_products: newItem.final_products,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteProcessingSoldData({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "sold_intermediate_product",
        header: t("Sold intermediate product"),
        type: "text",
        disableSortBy: true,
        placeholder: t("TYPE_HERE"),
        width: "100px",
      },
      {
        accessor: "manufacturing_process",
        header: `${t("Which manufacturing processes emit CO2?")}`,
        type: "text",
        disableSortBy: true,
        width: "130px",
        tipHeader: "Tip",
        tipBody: t("MANUFACTURING_PROCESS_DATA_TIP"),
      },
      {
        accessor: "manuel",
        header: `${t("Manual emission factor")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: "kg CO₂e/kg",
        tipHeader: "Tip",
        tipBody: t("MANUEL_PROCESS_TIP"),
      },
      {
        accessor: "weight_final_product",
        header: `${t("Weight of final product")}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: "kg",
        tipHeader: "Tip",
        tipBody: t("WEIGHT_FINAL_TIP"),
      },
      {
        accessor: "final_products",
        header: `${t(
          "How many final products include your intermediate product as a component?"
        )}`,
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        unit: "pcs",
      },
      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "40px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "90px",
      },
    ],
    [t]
  );

  const disableRules = {
    carbon: () => true,
  };

  const subtotalColumns = {
    carbon: { label: t("TOTAL_CARBON") },
  };

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      subtotalColumns={subtotalColumns}
      customInputs={{ carbon: CustomInputComponent }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
    />
  );
};

export default ProcessingDataTable;
